import { z } from "zod";
import { questionSchema, trainingSectionSchema } from "./catalog.js";
import { learningVideoSchema } from "./learning-video.js";

export const passportStableIdSchema = z
  .string()
  .min(1)
  .max(100)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);

export const domainValidationStatusSchema = z.enum([
  "pending_domain_validation",
  "validated_for_prototype",
  "approved",
]);
export const stampApplicabilitySchema = z.enum(["required", "optional", "out_of_scope"]);
export const evidenceGateStageSchema = z.enum(["not_required", "review_submission", "confirmation"]);
export const pathwayRelationTypeSchema = z.enum(["blocking", "recommended", "recurring"]);
export const atomicRelationTypeSchema = z.enum(["prerequisite", "recurring", "supports"]);
export const atomicCoverageStrengthSchema = z.enum(["indirect", "partial", "direct"]);

export const sourceProvenanceSchema = z
  .object({
    source: z.string().min(1),
    sourceId: z.string().min(1),
    sourceType: z.enum([
      "cluster",
      "capability_domain",
      "beroepshandeling",
      "vaardigheid",
      "kennis",
      "houding",
      "course",
      "learning_objective",
      "relation",
    ]),
  })
  .strict();

export const evidenceGateSchema = z
  .object({
    knowledge: evidenceGateStageSchema,
    practice: evidenceGateStageSchema,
    behaviour: evidenceGateStageSchema,
  })
  .strict();

export const passportPageSchema = z
  .object({
    id: passportStableIdSchema,
    title: z.string().min(1),
    beroepshandeling: z.string().min(1),
    context: z.string().min(1),
    regime: z.string().min(1),
    stampIds: z.array(passportStableIdSchema).min(1),
    validationStatus: domainValidationStatusSchema,
    coverageWarning: z.string().min(1),
    provenance: z.array(sourceProvenanceSchema).min(1),
    archived: z.boolean().default(false),
  })
  .strict();

export const stampCompletionPolicySchema = z
  .object({
    thresholdPercent: z.number().int().min(1).max(100),
    weights: z
      .object({
        skill: z.number().int().min(0).max(100),
        knowledge: z.number().int().min(0).max(100),
        behaviour: z.number().int().min(0).max(100),
      })
      .strict(),
    mandatoryRequirementIds: z.array(passportStableIdSchema),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.weights.skill + value.weights.knowledge + value.weights.behaviour !== 100) {
      context.addIssue({ code: "custom", path: ["weights"], message: "Weights must add up to 100." });
    }
  });

export const stampSchema = z
  .object({
    id: passportStableIdSchema,
    title: z.string().min(1),
    shortTitle: z.string().min(1),
    description: z.string().min(1),
    observableResult: z.string().min(1),
    pageIds: z.array(passportStableIdSchema).min(1),
    skillCriterionIds: z.array(passportStableIdSchema),
    knowledgeCriterionIds: z.array(passportStableIdSchema),
    behaviourCriterionIds: z.array(passportStableIdSchema),
    learningResourceIds: z.array(passportStableIdSchema),
    evidenceGate: evidenceGateSchema,
    completionPolicy: stampCompletionPolicySchema,
    validationStatus: domainValidationStatusSchema,
    provenance: z.array(sourceProvenanceSchema).min(1),
    archived: z.boolean().default(false),
  })
  .strict();

const criterionBaseSchema = z
  .object({
    id: passportStableIdSchema,
    title: z.string().min(1),
    stampIds: z.array(passportStableIdSchema).min(1),
    linkedRequirementIds: z.array(passportStableIdSchema),
    validationStatus: domainValidationStatusSchema,
    provenance: z.array(sourceProvenanceSchema).min(1),
    archived: z.boolean().default(false),
  })
  .strict();

export const knowledgeCriterionSchema = criterionBaseSchema.extend({
  kind: z.literal("knowledge"),
  knowledgeType: z.string().min(1),
});
export const skillCriterionSchema = criterionBaseSchema.extend({
  kind: z.literal("skill"),
  skillType: z.string().min(1),
  observableResult: z.string().min(1),
  recurring: z.boolean(),
});
export const behaviourCriterionSchema = criterionBaseSchema.extend({
  kind: z.literal("behaviour"),
  indicators: z.array(z.string().min(1)).min(1),
  allowDeferred: z.boolean(),
});
export const passportCriterionSchema = z.discriminatedUnion("kind", [
  skillCriterionSchema,
  knowledgeCriterionSchema,
  behaviourCriterionSchema,
]);

export const learningResourceCoverageSchema = z
  .object({
    criterionId: passportStableIdSchema,
    strength: atomicCoverageStrengthSchema,
    score: z.number().int().min(1).max(3),
    objectiveIds: z.array(z.string().min(1)),
  })
  .strict();

export const learningResourceSectionSchema = trainingSectionSchema.extend({
  // Older catalogue sections only have text. New course parts carry stable source IDs.
  id: z.string().min(1).optional(),
  video: learningVideoSchema.optional(),
  coverage: z
    .array(
      z
        .object({
          criterionId: passportStableIdSchema,
          strength: atomicCoverageStrengthSchema,
        })
        .strict(),
    )
    .optional(),
});

export const learningResourceSchema = z
  .object({
    id: passportStableIdSchema,
    title: z.string().min(1),
    description: z.string().min(1),
    minutes: z.number().int().positive().max(480),
    stampIds: z.array(passportStableIdSchema).min(1),
    criterionIds: z.array(passportStableIdSchema),
    coverage: z.array(learningResourceCoverageSchema),
    sections: z.array(learningResourceSectionSchema).min(1),
    provenance: z.array(sourceProvenanceSchema).min(1),
    archived: z.boolean().default(false),
  })
  .strict();

export const stampAssessmentSchema = z
  .object({
    id: passportStableIdSchema,
    stampId: passportStableIdSchema,
    title: z.string().min(1),
    passMark: z.number().int().min(1).max(100),
    questions: z.array(questionSchema).min(1),
    archived: z.boolean().default(false),
  })
  .strict();

export const stampPathwayRelationSchema = z
  .object({
    id: passportStableIdSchema,
    fromStampId: passportStableIdSchema,
    toStampId: passportStableIdSchema,
    type: pathwayRelationTypeSchema,
    explanation: z.string().min(1),
    provenance: z.array(sourceProvenanceSchema).min(1),
    archived: z.boolean().default(false),
  })
  .strict();

export const atomicRequirementRelationSchema = z
  .object({
    id: passportStableIdSchema,
    stampId: passportStableIdSchema,
    fromRequirementId: passportStableIdSchema,
    toRequirementId: passportStableIdSchema,
    type: atomicRelationTypeSchema,
    explanation: z.string().min(1),
    provenance: z.array(sourceProvenanceSchema).min(1),
    archived: z.boolean().default(false),
  })
  .strict();

export const roleStampApplicabilitySchema = z
  .object({
    roleId: passportStableIdSchema,
    pageId: passportStableIdSchema,
    stampId: passportStableIdSchema.nullable(),
    applicability: stampApplicabilitySchema,
  })
  .strict();

export const passportCatalogueSchema = z
  .object({
    pages: z.array(passportPageSchema),
    stamps: z.array(stampSchema),
    criteria: z.array(passportCriterionSchema),
    learningResources: z.array(learningResourceSchema),
    assessments: z.array(stampAssessmentSchema),
    relations: z.array(stampPathwayRelationSchema),
    atomicRelations: z.array(atomicRequirementRelationSchema),
    applicability: z.array(roleStampApplicabilitySchema),
  })
  .strict();

export type SourceProvenance = z.infer<typeof sourceProvenanceSchema>;
export type StampApplicability = z.infer<typeof stampApplicabilitySchema>;
export type PassportPage = z.infer<typeof passportPageSchema>;
export type Stamp = z.infer<typeof stampSchema>;
export type PassportCriterion = z.infer<typeof passportCriterionSchema>;
export type LearningResource = z.infer<typeof learningResourceSchema>;
export type LearningResourceCoverage = z.infer<typeof learningResourceCoverageSchema>;
export type LearningResourceSection = z.infer<typeof learningResourceSectionSchema>;
export type StampCompletionPolicy = z.infer<typeof stampCompletionPolicySchema>;
export type AtomicRequirementRelation = z.infer<typeof atomicRequirementRelationSchema>;
export type StampAssessment = z.infer<typeof stampAssessmentSchema>;
export type StampPathwayRelation = z.infer<typeof stampPathwayRelationSchema>;
export type RoleStampApplicability = z.infer<typeof roleStampApplicabilitySchema>;
export type PassportCatalogue = z.infer<typeof passportCatalogueSchema>;
